import { Button } from "@package/subframe/components/Button";
import { type ReactNode, useEffect, useRef } from "react";

interface ProfileSectionFormProps {
  testId: string;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  /** Extra reasons to hold Save back, e.g. a photo still uploading. */
  saveDisabled?: boolean;
  children: ReactNode;
}

const FIRST_FIELD =
  "input:not([type='file']):not([type='hidden']), textarea, button[role='combobox'], [role='radio']";

/**
 * A section's edit form, shown in place of its fields inside the card, so it
 * is masked by the card. The first field takes focus when it opens, and Escape
 * cancels.
 */
export function ProfileSectionForm({
  testId,
  isPending,
  onCancel,
  onSubmit,
  saveDisabled = false,
  children,
}: ProfileSectionFormProps) {
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    formRef.current?.querySelector<HTMLElement>(FIRST_FIELD)?.focus();
  }, []);

  // Kept open until the save settles, so its error can't land on a closed form.
  const cancel = () => {
    if (isPending) return;
    onCancel();
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Escape") return;
    if (event.defaultPrevented) return;
    event.preventDefault();
    cancel();
  };

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      onKeyDown={handleKeyDown}
      noValidate
      className="flex w-full flex-col gap-6"
      data-testid={testId}
    >
      <div className="flex w-full flex-col gap-6">{children}</div>
      <div className="flex w-full items-center justify-end gap-3">
        <Button
          type="button"
          variant="neutral-secondary"
          size="medium"
          onClick={cancel}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="brand-primary"
          size="medium"
          loading={isPending}
          disabled={isPending || saveDisabled}
        >
          Save changes
        </Button>
      </div>
    </form>
  );
}
